import { useEffect, useRef, useState } from "react";
import { Bold, Italic, List, Underline } from "lucide-react";
import Button from "../../../../components/Button/Button";
import { sanitizeHtml } from "../../../../utils/sanitizeHtml";

const TOOLS = [
  { command: "bold", icon: Bold, label: "Bold" },
  { command: "italic", icon: Italic, label: "Italic" },
  { command: "underline", icon: Underline, label: "Underline" },
  { command: "insertUnorderedList", icon: List, label: "Bullet list", divider: true },
];

// New lines as <p>, and <b>/<i>/<u> tags rather than inline styles (matches the API format)
const configureEditing = () => {
  document.execCommand("defaultParagraphSeparator", false, "p");
  document.execCommand("styleWithCSS", false, false);
};

const getActiveCommands = () =>
  TOOLS.filter(({ command }) => document.queryCommandState(command)).map(({ command }) => command);

/** Minimal rich-text box (bold / italic / underline / bullets) that emits HTML. */
const RichTextEditor = ({ id, value, onChange, placeholder = "Write your content here ...", autoFocus = false }) => {
  const editorRef = useRef(null);
  const [activeCommands, setActiveCommands] = useState([]);

  // Only push outside changes into the DOM, never while typing (keeps the caret in place)
  useEffect(() => {
    const editor = editorRef.current;
    const safeValue = sanitizeHtml(value || "");
    if (editor && editor.innerHTML !== safeValue) editor.innerHTML = safeValue;
  }, [value]);

  useEffect(() => {
    if (autoFocus) editorRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const emitChange = () => {
    const html = editorRef.current.innerHTML;
    // An emptied editor leaves "<br>" behind; treat it as empty
    const hasContent = editorRef.current.textContent.trim() !== "" || html.includes("<li");
    onChange(hasContent ? html : "");
  };

  const runCommand = (command) => {
    editorRef.current.focus();
    document.execCommand(command, false);
    emitChange();
    setActiveCommands(getActiveCommands());
  };

  return (
    <div className="lp-rte">
      <div className="lp-rte__toolbar" role="toolbar" aria-label="Formatting">
        {TOOLS.map(({ command, icon: Icon, label, divider }) => (
          <span key={command} className="d-inline-flex align-items-center">
            {divider && <span className="lp-rte__divider" />}
            <Button
              variant="plain"
              className={`lp-btn-plain ${activeCommands.includes(command) ? "is-active" : ""}`}
              // Keep the text selection when clicking a toolbar button
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => runCommand(command)}
              aria-label={label}
              aria-pressed={activeCommands.includes(command)}
              title={label}
              icon={<Icon size={14} />}
            />
          </span>
        ))}
      </div>
      <div
        id={id}
        ref={editorRef}
        className="lp-rte__content"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onFocus={configureEditing}
        onInput={emitChange}
        onKeyUp={() => setActiveCommands(getActiveCommands())}
        onMouseUp={() => setActiveCommands(getActiveCommands())}
      />
    </div>
  );
};

export default RichTextEditor;
